import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, FileText } from 'lucide-react';
import { labReportService, labOrderService, LabReport, LabOrder } from '../../lib/services.lab';

export default function PublicVerify() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [report, setReport] = useState<LabReport | null>(null);
  const [order, setOrder] = useState<LabOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    verifyReport();
  }, [reportId, token]);

  const verifyReport = async () => {
    if (!reportId) {
      setError('Report ID is required');
      setLoading(false);
      return;
    }

    try {
      const reportData = await labReportService.getByOrder(reportId);
      
      if (!reportData) {
        setError('Report not found');
        setLoading(false);
        return;
      }

      setReport(reportData);

      const orderData = await labOrderService.getById(reportId);
      if (orderData) {
        setOrder(orderData);
      }

      setVerified(true);
    } catch (err) {
      console.error('Verification error:', err);
      setError('Failed to verify report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <FileText className="mx-auto h-12 w-12 text-blue-600" />
          <h1 className="mt-4 text-3xl font-bold text-gray-900">Lab Report Verification</h1>
          <p className="mt-2 text-sm text-gray-600">
            Verify the authenticity of laboratory reports
          </p>
        </div>

        {error ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <XCircle className="mx-auto h-16 w-16 text-red-600 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        ) : verified && report ? (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="bg-green-50 border-b border-green-200 p-6">
              <div className="flex items-center justify-center">
                <CheckCircle className="h-16 w-16 text-green-600 mr-4" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Report Verified</h2>
                  <p className="text-sm text-gray-600">This is an authentic laboratory report</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Details</h3>
                <dl className="grid grid-cols-1 gap-4">
                  <div className="border-b pb-3">
                    <dt className="text-sm font-medium text-gray-500">Report Number</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">{report.report_no}</dd>
                  </div>
                  
                  {order?.patient && (
                    <div className="border-b pb-3">
                      <dt className="text-sm font-medium text-gray-500">Patient Name</dt>
                      <dd className="mt-1 text-sm text-gray-900">{order.patient.name}</dd>
                    </div>
                  )}

                  <div className="border-b pb-3">
                    <dt className="text-sm font-medium text-gray-500">Finalized Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(report.finalized_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </dd>
                  </div>

                  <div className="border-b pb-3">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1">
                      <span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
                        Verified & Finalized
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Verification Complete</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        This report has been digitally verified and is an authentic document issued by our
                        laboratory. The QR code and report number can be used to verify this report at any time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-8 text-center text-sm text-gray-500">
          <p>For any questions or concerns, please contact the laboratory directly.</p>
        </div>
      </div>
    </div>
  );
}
